import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Users, Search, Plus, Edit3, Trash2, UserPlus, Filter } from 'lucide-react';

const UserManagement = ({ onStatsUpdate }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const roles = ['admin', 'agent', 'notary', 'client'];

  const fetchUsers = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });
      if (filterRole !== 'all') {
        query = query?.eq('role', filterRole);
      }
      const { data, error } = await query;
      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filterRole]);

  const filteredUsers = users?.filter(user => 
    user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    user?.phone?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const handleSaveUser = async (userData) => {
    try {
      if (editingUser) {
        const { error } = await supabase?.from('user_profiles')?.update(userData)?.eq('id', editingUser?.id);
        if (error) throw error;
      } else {
        const { error } = await supabase?.from('user_profiles')?.insert([userData]);
        if (error) throw error;
      }
      setShowAddModal(false);
      setEditingUser(null);
      fetchUsers();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Erreur lors de l\'enregistrement de l\'utilisateur');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    try {
      const { error } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId);
      if (error) throw error;
      fetchUsers();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ role: newRole })?.eq('id', userId);
      if (error) throw error;
      fetchUsers();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Erreur lors de la mise à jour du rôle');
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: !isActive })?.eq('id', userId);
      if (error) throw error;
      fetchUsers();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      agent: 'bg-blue-100 text-blue-800',
      notary: 'bg-green-100 text-green-800',
      client: 'bg-gray-100 text-gray-800'
    };
    return colors?.[role] || 'bg-gray-100 text-gray-800';
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrateur',
      agent: 'Agent',
      notary: 'Notaire',
      client: 'Client'
    };
    return labels?.[role] || role;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)]?.map((_, i) => (
          <div key={i} className="bg-card border border-border p-6 rounded-lg animate-pulse">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-muted rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-1/4 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/3"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-card border border-border p-4 sm:p-6 rounded-lg">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Users className="w-5 h-5 text-brand-gold flex-shrink-0" />
                Gestion des Utilisateurs
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                {filteredUsers?.length} utilisateur(s) trouvé(s)
              </p>
            </div>
            
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-earth text-white rounded-lg hover:bg-brand-earth/90 transition-colors min-h-[44px] touch-manipulation text-sm font-medium w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <span>Ajouter Utilisateur</span>
            </button>
          </div>

          {/* Search + Filter row */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher utilisateurs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
              />
            </div>
            
            <div className="relative sm:w-48">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e?.target?.value)}
                className="w-full pl-10 pr-8 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent appearance-none min-h-[44px]"
              >
                <option value="all">Tous les rôles</option>
                {roles?.map(role => (
                  <option key={role} value={role}>{getRoleLabel(role)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table — scrollable on mobile */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
          <table className="min-w-full divide-y divide-border" style={{ minWidth: '640px' }}>
            <thead className="bg-muted/40">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  Utilisateur
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  Rôle
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                  Contact
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  Statut
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
                  Inscription
                </th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {filteredUsers?.map((user) => (
                <tr key={user?.id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-brand-gold/10 border border-brand-gold/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-brand-gold">
                          {user?.full_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate max-w-[120px] sm:max-w-none">{user?.full_name}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[120px] sm:max-w-none">{user?.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <select
                      value={user?.role}
                      onChange={(e) => handleRoleChange(user?.id, e?.target?.value)}
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user?.role)} border-none cursor-pointer min-h-[32px]`}
                    >
                      {roles?.map(role => (
                        <option key={role} value={role}>{getRoleLabel(role)}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-foreground hidden md:table-cell">
                    {user?.phone || <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(user?.id, user?.is_active)}
                      className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full min-h-[28px] items-center touch-manipulation ${
                        user?.is_active
                          ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user?.is_active ? 'Actif' : 'Inactif'}
                    </button>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-muted-foreground hidden lg:table-cell">
                    {new Date(user?.created_at)?.toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => { setEditingUser(user); setShowAddModal(true); }}
                        className="text-brand-earth hover:text-brand-earth/80 p-2 rounded-lg hover:bg-brand-earth/10 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center touch-manipulation"
                        aria-label="Modifier"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user?.id)}
                        className="text-destructive hover:text-destructive/80 p-2 rounded-lg hover:bg-destructive/10 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center touch-manipulation"
                        aria-label="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredUsers?.length === 0 && (
            <div className="text-center py-12 px-4">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-sm">Aucun utilisateur trouvé</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-brand-earth text-white rounded-lg hover:bg-brand-earth/90 transition-colors text-sm min-h-[44px] touch-manipulation"
              >
                <UserPlus className="w-4 h-4" />
                <span>Ajouter le premier utilisateur</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <UserModal
          user={editingUser}
          roles={roles}
          onSave={handleSaveUser}
          onClose={() => { setShowAddModal(false); setEditingUser(null); }}
        />
      )}
    </div>
  );
};

// User Modal Component
const UserModal = ({ user, roles, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    role: user?.role || 'client',
    is_active: user?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(formData);
  };

  const getRoleLabel = (role) => {
    const labels = { admin: 'Administrateur', agent: 'Agent', notary: 'Notaire', client: 'Client' };
    return labels?.[role] || role;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-card border border-border rounded-xl shadow-xl w-full max-w-lg mx-auto">
          <form onSubmit={handleSubmit}>
            <div className="p-5 sm:p-6">
              <h3 className="text-lg font-semibold text-foreground mb-5">
                {user ? 'Modifier Utilisateur' : 'Ajouter Utilisateur'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Nom complet *</label>
                  <input
                    type="text"
                    required
                    value={formData?.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e?.target?.value})}
                    className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData?.email}
                    onChange={(e) => setFormData({...formData, email: e?.target?.value})}
                    className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Téléphone</label>
                  <input
                    type="tel"
                    value={formData?.phone}
                    onChange={(e) => setFormData({...formData, phone: e?.target?.value})}
                    className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Rôle *</label>
                  <select
                    value={formData?.role}
                    onChange={(e) => setFormData({...formData, role: e?.target?.value})}
                    className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent min-h-[44px]"
                  >
                    {roles?.map(role => (
                      <option key={role} value={role}>{getRoleLabel(role)}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData?.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e?.target?.checked})}
                    className="h-4 w-4 text-brand-earth focus:ring-ring border-border rounded"
                  />
                  <label htmlFor="is_active" className="text-sm text-foreground cursor-pointer">
                    Utilisateur actif
                  </label>
                </div>
              </div>
            </div>
            
            <div className="bg-muted/30 border-t border-border px-5 sm:px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground bg-background hover:bg-muted/40 transition-colors min-h-[44px] touch-manipulation"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-2.5 bg-brand-earth text-white rounded-lg text-sm font-medium hover:bg-brand-earth/90 transition-colors min-h-[44px] touch-manipulation"
              >
                {user ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;